import { cssMerge } from '@volvo-cars/css/utils';
import { useComposedRefs } from '@volvo-cars/react-utils';
import type React from 'react';
import { forwardRef, useEffect, useRef } from 'react';
import { ErrorMessage } from './error-message';
import { Hint } from './hint';
import type { BaseInputProps, GlobalHTMLAttributes } from './types';
import { useIds } from './use-ids';

export interface CheckboxProps extends GlobalHTMLAttributes, BaseInputProps {
  /**
   * The name of the input to use when submitting the form.
   */
  name: string;

  /**
   * Concise label for the checkbox.
   *
   * Labels should not contain HTML, add any required links in the `hint` instead.
   */
  label: string;

  /**
   * The value of the checkbox that will be submitted with the form data.
   *
   * @default on
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#value
   */
  value?: string;

  /**
   * Makes the checkbox required.
   */
  required?: boolean;

  /**
   * Set the error message of a checkbox and mark it invalid.
   */
  errorMessage?: string;

  /**
   * Force the checkbox to be invalid.
   *
   * @default false
   */
  'aria-invalid'?: boolean;

  /**
   * Whether the checkbox is checked.
   * Makes the input controlled.
   */
  checked?: boolean;

  /**
   * Fires when the input is checked or unchecked.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  /**
   * Whether an (uncontrolled) checkbox is checked by default.
   */
  defaultChecked?: boolean;

  /**
   * Indicates if the checkbox is in an indeterminate state, often used
   * to show a "partially selected" state (e.g., when some child checkboxes
   * are selected). This state is visual-only and does not affect the
   * checkbox's submitted value.
   *
   * @default false
   */
  indeterminate?: boolean;
}

/**
 * Checkboxes should be used for selecting one or more of many options, or as a toggle with a favourable option.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox(
    {
      label,
      id,
      hint,
      hidden,
      dir,
      lang,
      translate,
      slot,
      style,
      errorMessage: errorMessageProp,
      className,
      indeterminate,
      ...props
    }: CheckboxProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) {
    const { inputId, hintId, errorId } = useIds(id);
    const internalRef = useRef<HTMLInputElement | null>(null);

    const combinedRef = useComposedRefs(ref, internalRef);

    useEffect(() => {
      if (indeterminate !== undefined && internalRef.current) {
        internalRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    // Hide error message and error state when input is disabled
    const errorMessage = !props.disabled ? errorMessageProp : undefined;
    const ariaInvalid = props['aria-invalid'] || !!errorMessage;

    return (
      <div
        className={cssMerge('flex-row', className)}
        hidden={hidden}
        dir={dir}
        lang={lang}
        translate={translate}
        slot={slot}
        style={style}
      >
        <input
          {...props}
          ref={combinedRef}
          type="checkbox"
          id={inputId}
          aria-invalid={ariaInvalid ? true : undefined}
          aria-errormessage={errorMessage ? errorId : undefined}
          aria-describedby={[errorMessage && errorId, hint && hintId]
            .filter(Boolean)
            .join(' ')}
        />
        <div className="flex-col stack-4 ml-8">
          <label htmlFor={inputId} className="pl-8 -ml-8">
            {label}
          </label>
          <ErrorMessage errorMessage={errorMessage} id={errorId} />
          {hint && <Hint id={hintId}>{hint}</Hint>}
        </div>
      </div>
    );
  },
);
